import { useState } from 'react';
import { formatRoute, type TenantTab } from '../../app/router';
import { Drawer } from '../../design-system/Overlay';
import { PageHeader, Pill, QuickFilterTabs } from '../../design-system/primitives';
import { errorView, Loading } from '../../states/States';
import { useLoad } from '../../views/useLoad';
import { useAppServices } from '../../views/ViewContext';
import { CompositionTab } from './CompositionTab';
import { OrganizationTab } from './OrganizationTab';
import { PanelHistory } from './PanelHistory';
import { ParametersTab } from './ParametersTab';
import { errorMessage, LOCALES, type TenantProfile } from './tenant-data';
import './tenant.css';

/**
 * Page Tenant (ADR-128) : compte, sociétés et sites, paramètres (l'ancienne page Paramètres, ADR-081) et
 * composition du modèle. Toute écriture passe par une commande `configure`, autorisée et auditée par le
 * serveur, sous élévation ; l'écran n'autorise rien. Aucun identifiant interne n'est affiché.
 */
const TABS: ReadonlyArray<{ id: TenantTab; label: string }> = [
  { id: 'account', label: 'Compte' },
  { id: 'companies', label: 'Sociétés' },
  { id: 'sites', label: 'Sites' },
  { id: 'parameters', label: 'Paramètres' },
  { id: 'composition', label: 'Composition' },
];

const ENVIRONMENTS: Readonly<Record<string, string>> = { prod: 'Production', test: 'Test', sandbox: 'Bac à sable', dev: 'Développement' };

export function TenantPage({ tab }: { tab: TenantTab }) {
  const services = useAppServices();
  const [loaded, reload] = useLoad<TenantProfile>('tenant-profile', async () => (await services.client.query<TenantProfile>('GetTenantProfile')).data);
  if (loaded.status === 'loading') return <Loading />;
  if (loaded.status === 'error') return <>{errorView(loaded.error, reload)}</>;
  const profile = loaded.value;
  const counts: Partial<Record<TenantTab, number>> = { companies: profile.entities.length, sites: profile.sites.length };
  return (
    <section aria-labelledby="ay-tenant-title" className="ay-tenant">
      <PageHeader
        titleId="ay-tenant-title"
        eyebrow="Administration"
        title={profile.tenant.display_name}
        subtitle="Le compte, les sociétés et leurs sites, les paramètres et la composition du modèle."
        badge={
          <span className="ay-tenant__badges">
            <Pill tone={profile.tenant.status === 'active' ? 'success' : 'warn'}>{profile.tenant.status === 'active' ? 'Actif' : profile.tenant.status}</Pill>
            <Pill>{ENVIRONMENTS[profile.tenant.environment] ?? profile.tenant.environment}</Pill>
          </span>
        }
      />
      <QuickFilterTabs
        label="Sections du tenant"
        items={TABS}
        selected={tab}
        counts={counts}
        onSelect={(id) => {
          window.location.hash = formatRoute({ kind: 'tenant', tab: id as TenantTab });
        }}
      />
      {tab === 'account' && <AccountTab profile={profile} onSaved={reload} />}
      {(tab === 'companies' || tab === 'sites') && <OrganizationTab profile={profile} onSaved={reload} view={tab} />}
      {tab === 'parameters' && <ParametersTab onSaved={reload} />}
      {tab === 'composition' && <CompositionTab />}
    </section>
  );
}

/** Message d'erreur d'une commande ; un refus de politique propose d'élever la session. */
export function CommandError({ error }: { error: { message: string; elevation: boolean } | null }) {
  if (error === null) return null;
  return (
    <p role="alert" className="ay-field__error">
      {error.message}
      {error.elevation && (
        <>
          {' '}
          <a href={formatRoute({ kind: 'account' })}>Élever la session</a> puis recommencer.
        </>
      )}
    </p>
  );
}

function AccountTab({ profile, onSaved }: { profile: TenantProfile; onSaved: () => void }) {
  const services = useAppServices();
  const { tenant, parameters } = profile;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tenant.display_name);
  const [locale, setLocale] = useState(tenant.default_locale ?? 'fr');
  const [error, setError] = useState<{ message: string; elevation: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const currencies = Array.isArray(parameters['core.currencies']) ? (parameters['core.currencies'] as string[]) : [];
  const defaultCurrency = typeof parameters['core.default_currency'] === 'string' ? parameters['core.default_currency'] : undefined;
  const save = async (): Promise<void> => {
    const payload: Record<string, unknown> = {};
    if (name.trim() !== tenant.display_name) payload['display_name'] = name.trim();
    if (locale !== tenant.default_locale) payload['default_locale'] = locale;
    if (Object.keys(payload).length === 0) return setEditing(false);
    setBusy(true);
    setError(null);
    try {
      await services.client.command('UpdateTenantProfile', { payload, target: { type: 'Tenant', id: tenant.id }, expected_version: tenant.version });
      setEditing(false);
      onSaved();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const parametersLink = <a href={formatRoute({ kind: 'tenant', tab: 'parameters' })}>réglé dans Paramètres</a>;
  return (
    <section className="ay-card ay-tenant__account" aria-labelledby="ay-tenant-account">
      <div className="ay-tenant__cardhead">
        <h2 id="ay-tenant-account">Compte</h2>
        <button
          type="button"
          className="ay-button ay-button--ghost"
          aria-haspopup="dialog"
          onClick={() => {
            setName(tenant.display_name);
            setLocale(tenant.default_locale ?? 'fr');
            setError(null);
            setEditing(true);
          }}
        >
          Modifier
        </button>
      </div>
      {/* Modification dans le panneau à droite, comme les sociétés et les sites (remarque de l'utilisateur du 2026-10-15). */}
      <Drawer title="Modifier le compte" kicker="Compte" open={editing} onClose={() => setEditing(false)} modal>
        <div className="ay-tenant__panel">
        <form
          className="ay-tenant__form"
          aria-label="Modifier le compte"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <label className="ay-field ay-tenant__wide">
            <span className="ay-field__label">Nom</span>
            <input value={name} required maxLength={200} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="ay-field">
            <span className="ay-field__label">Langue par défaut</span>
            <select value={locale} onChange={(event) => setLocale(event.target.value)}>
              {LOCALES.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>
          <p className="ay-text ay-text--muted ay-tenant__wide">Le code, le statut et l’environnement relèvent de la plateforme. Le fuseau et les devises se règlent dans l’onglet Paramètres.</p>
          <CommandError error={error} />
          <div className="ay-tenant__actions ay-tenant__wide">
            <button type="button" className="ay-button ay-button--ghost" onClick={() => setEditing(false)}>
              Annuler
            </button>
            <button type="submit" className="ay-button ay-button--primary" disabled={busy || name.trim() === ''}>
              Enregistrer
            </button>
          </div>
        </form>
        <PanelHistory type="Tenant" id={tenant.id} version={tenant.version} />
        </div>
      </Drawer>
        <dl className="ay-tenant__facts">
          <dt>Nom</dt>
          <dd>{tenant.display_name}</dd>
          {tenant.code !== undefined && (
            <>
              <dt>Code</dt>
              <dd className="ay-tenant__mono">{tenant.code}</dd>
            </>
          )}
          <dt>Statut</dt>
          <dd>{tenant.status === 'active' ? 'Actif' : tenant.status}</dd>
          <dt>Environnement</dt>
          <dd>{ENVIRONMENTS[tenant.environment] ?? tenant.environment}</dd>
          <dt>Langue par défaut</dt>
          <dd>{LOCALES.find((entry) => entry.value === (tenant.default_locale ?? 'fr'))?.label ?? tenant.default_locale}</dd>
          <dt>Fuseau</dt>
          <dd>
            {String(parameters['core.default_timezone'] ?? '—')} <span className="ay-text--muted">· {parametersLink}</span>
          </dd>
          <dt>Devises</dt>
          <dd>
            {currencies.length === 0 ? '—' : currencies.map((code) => (code === defaultCurrency ? `${code} (par défaut)` : code)).join(', ')} <span className="ay-text--muted">· réglées dans Paramètres</span>
          </dd>
        </dl>
    </section>
  );
}
